import { useState, useEffect } from 'react';
import { ScrollView, ActivityIndicator } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import AppSafeArea from '@/components/layout/AppSafeArea';
import { useRouter } from 'expo-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Mail, Save, Camera, LogOut, Bell, Shield, ClipboardList, MessageSquareMore, ChevronRight } from 'lucide-react-native';
import { useTheme } from '@/lib/styling';
import { Theme } from '@/theme';

// UI Components
import { Box, Text, PressableBox } from '@/components/ui/primitives';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Button } from '@/components/ui/Button';
import { Avatar, AvatarImage } from '@/components/ui/Avatar';
import { AppSelect } from '@/components/ui/AppSelect';
import { Switch } from '@/components/ui/Switch';

// Hooks and Utils
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { useImagePicker } from '@/hooks/useImagePicker';
import { useProfileImage } from '@/hooks/useProfileImage';
import { useTRPCClient } from '@/lib/trpc';
import { isUnauthorizedError } from '@/lib/authUtils';
import { clientLogger } from '@/lib/datadog';
import AppHeader from '@/components/AppHeader';
import { betterAuthClient } from '@/lib/betterAuthClient';
import { getLandingHref, getWeekManagementHref, getBetsCreatorHref, getFeedbackHref } from '@/lib/routes';

const NO_DEFAULT_LEAGUE_VALUE = 'none';

export default function ProfileScreen() {
  const router = useRouter();
  const { user, isLoading: isAuthLoading, isAuthenticated } = useAuth();
  const trpcClient = useTRPCClient();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const theme = useTheme<Theme>();

  const isAdmin = Boolean(user?.isAdmin);
  const isBetsAdmin = Boolean(user?.isBetsAdmin);

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [defaultLeagueId, setDefaultLeagueId] = useState<string | null>(null);
  const [pushPicksRemindersEnabled, setPushPicksRemindersEnabled] = useState(true);
  const [pushScoringChangesEnabled, setPushScoringChangesEnabled] = useState(true);
  const [pushChatMessagesEnabled, setPushChatMessagesEnabled] = useState(true);
  const [pushLiveGameAlertsEnabled, setPushLiveGameAlertsEnabled] = useState(true);
  const [pushMyPickUpdatesEnabled, setPushMyPickUpdatesEnabled] = useState(true);
  const [pushFinalScoresEnabled, setPushFinalScoresEnabled] = useState(true);
  const [liveNotificationDelayMinutes, setLiveNotificationDelayMinutes] = useState(0);

  // Track if form has changes
  const [hasChanges, setHasChanges] = useState(false);

  // Image picker and upload hooks
  const { pickImage } = useImagePicker();
  const { uploadProfileImageAsync } = useProfileImage();

  // Initialize form values when user data loads
  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || '');
      setLastName(user.lastName || '');
      setProfileImageUrl(user.profileImageUrl || '');
      setDefaultLeagueId(user.defaultLeagueId ?? null);
      setPushPicksRemindersEnabled(user.pushPicksRemindersEnabled ?? true);
      setPushScoringChangesEnabled(user.pushScoringChangesEnabled ?? true);
      setPushChatMessagesEnabled(user.pushChatMessagesEnabled ?? true);
      setPushLiveGameAlertsEnabled(user.pushLiveGameAlertsEnabled ?? true);
      setPushMyPickUpdatesEnabled(user.pushMyPickUpdatesEnabled ?? true);
      setPushFinalScoresEnabled(user.pushFinalScoresEnabled ?? true);
      setLiveNotificationDelayMinutes(user.liveNotificationDelayMinutes ?? 0);
    }
  }, [user]);

  // Track form changes
  useEffect(() => {
    if (user) {
      const changed =
        firstName !== (user.firstName || '') ||
        lastName !== (user.lastName || '') ||
        profileImageUrl !== (user.profileImageUrl || '') ||
        (defaultLeagueId ?? null) !== (user.defaultLeagueId ?? null) ||
        pushPicksRemindersEnabled !== (user.pushPicksRemindersEnabled ?? true) ||
        pushScoringChangesEnabled !== (user.pushScoringChangesEnabled ?? true) ||
        pushChatMessagesEnabled !== (user.pushChatMessagesEnabled ?? true) ||
        pushLiveGameAlertsEnabled !== (user.pushLiveGameAlertsEnabled ?? true) ||
        pushMyPickUpdatesEnabled !== (user.pushMyPickUpdatesEnabled ?? true) ||
        pushFinalScoresEnabled !== (user.pushFinalScoresEnabled ?? true) ||
        liveNotificationDelayMinutes !== (user.liveNotificationDelayMinutes ?? 0);
      setHasChanges(changed);
    }
  }, [
    firstName,
    lastName,
    profileImageUrl,
    defaultLeagueId,
    pushPicksRemindersEnabled,
    pushScoringChangesEnabled,
    pushChatMessagesEnabled,
    pushLiveGameAlertsEnabled,
    pushMyPickUpdatesEnabled,
    pushFinalScoresEnabled,
    liveNotificationDelayMinutes,
    user,
  ]);

  // Profile update mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (data: {
      firstName: string;
      lastName: string;
      profileImageUrl: string;
      defaultLeagueId: string | null;
      pushPicksRemindersEnabled: boolean;
      pushScoringChangesEnabled: boolean;
      pushChatMessagesEnabled: boolean;
      pushLiveGameAlertsEnabled: boolean;
      pushMyPickUpdatesEnabled: boolean;
      pushFinalScoresEnabled: boolean;
      liveNotificationDelayMinutes: number;
    }) => {
      return trpcClient.users.updateProfile.mutate(data);
    },
    onSuccess: (updatedUser) => {
      // Update the user cache
      queryClient.setQueryData(['/api/auth/user'], updatedUser);
      queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });

      toast.show({
        type: 'success',
        text1: 'Profile Updated',
        text2: 'Your profile has been updated successfully',
      });

      setHasChanges(false);
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({
          type: 'error',
          text1: 'Unauthorized',
          text2: 'You are logged out. Please login again.',
        });
        return;
      }

      toast.show({
        type: 'error',
        text1: 'Update Failed',
        text2: 'Failed to update profile',
      });
    },
  });

  const handleSaveProfile = () => {
    // Validate inputs
    if (!firstName.trim() || !lastName.trim()) {
      toast.show({
        type: 'error',
        text1: 'Validation Error',
        text2: 'First name and last name are required',
      });
      return;
    }

    updateProfileMutation.mutate({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      profileImageUrl: profileImageUrl.trim(),
      defaultLeagueId: defaultLeagueId ?? null,
      pushPicksRemindersEnabled,
      pushScoringChangesEnabled,
      pushChatMessagesEnabled,
      pushLiveGameAlertsEnabled,
      pushMyPickUpdatesEnabled,
      pushFinalScoresEnabled,
      liveNotificationDelayMinutes,
    });
  };

  const handleUploadPhoto = async () => {
    if (!user) return;

    try {
      // Open image picker
      const image = await pickImage();
      if (!image) return; // User cancelled

      // Show upload progress
      toast.show({
        type: 'info',
        text1: 'Uploading photo...',
        text2: 'Please wait while we upload your image',
      });

      // Upload image
      const result = await uploadProfileImageAsync({
        image,
        userId: user.id
      });

      // Update local state optimistically
      if (result?.confirmedPath) {
        setProfileImageUrl(result.confirmedPath);
      }

      // Success toast is shown by the useProfileImage hook
    } catch (error) {
      // Error toast is shown by the useProfileImage hook
      clientLogger.error('Upload photo error', { error });
    }
  };

  const getInitials = () => {
    const first = firstName?.charAt(0)?.toUpperCase() || user?.firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || user?.lastName?.charAt(0)?.toUpperCase() || '';
    return first + last || 'U';
  };

  const formatMemberSince = (dateString: string | undefined): string => {
    if (!dateString) return 'Unknown';

    try {
      const date = new Date(dateString);
      return format(date, 'MMMM yyyy');
    } catch {
      return 'Unknown';
    }
  };

  const handleLogout = async () => {
    try {
      const result = await betterAuthClient.signOut();
      if (result.error) {
        throw new Error(result.error.message || 'Logout failed');
      }
    } catch (error) {
      clientLogger.error('Logout failed', { error });
    } finally {
      queryClient.clear();
      router.replace(getLandingHref());
    }
  };

  // Handle loading state
  if (isAuthLoading) {
    return (
      <AppSafeArea edges={['left', 'right', 'bottom']}>
        <AppHeader title="Profile" allowBack={false} rightAction="close" />
        <Box flex={1} alignItems="center" justifyContent="center" gap="m">
          <ActivityIndicator size="large" color={theme.colors.press} />
          <Text variant="caption" color="pressMuted">Loading profile...</Text>
        </Box>
      </AppSafeArea>
    );
  }

  // Handle unauthenticated state
  if (!isAuthenticated || !user) {
    return (
      <AppSafeArea edges={['left', 'right', 'bottom']}>
        <AppHeader title="Profile" allowBack={false} rightAction="close" />
        <Box flex={1} alignItems="center" justifyContent="center" gap="m" paddingHorizontal="l">
          <Text variant="title" fontWeight="bold">Not Authenticated</Text>
          <Text variant="body" color="pressMuted" textAlign="center">
            Please log in to view your profile
          </Text>
          <Button onPress={() => router.back()} marginTop="m" paddingHorizontal="l">
            <Text variant="button">Go Back</Text>
          </Button>
        </Box>
      </AppSafeArea>
    );
  }

  return (
    <AppSafeArea edges={['left', 'right', 'bottom']}>
      <AppHeader title="Profile" allowBack={false} rightAction="close" />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
      >
        <Box paddingHorizontal="m" paddingTop="l" paddingBottom="m" alignItems="center">
          <Avatar size={112} backgroundColor="primary" alignItems="center" justifyContent="center">
            {profileImageUrl ? (
              <AvatarImage src={profileImageUrl} />
            ) : (
              <Text variant="h1" color="white">{getInitials()}</Text>
            )}
          </Avatar>
          <Text variant="title" fontWeight="semibold" marginTop="m">
            {[firstName, lastName].filter(Boolean).join(' ') || 'Your profile'}
          </Text>
          <Text variant="caption" color="pressMuted" marginTop="xs">
            Member since {formatMemberSince(user.createdAt)}
          </Text>
          <Button variant="outline" onPress={handleUploadPhoto} marginTop="m">
            <Box flexDirection="row" alignItems="center" justifyContent="center" gap="s">
              <Camera size={16} color={theme.colors.text} />
              <Text variant="button" color="text">Upload photo</Text>
            </Box>
          </Button>
          <Text variant="caption" textAlign="center" marginTop="s">
            Square image, max 5MB
          </Text>
        </Box>

        {/* Personal Information */}
        <Card marginHorizontal="m" marginBottom="m">
          <CardHeader>
            <Text variant="caption">Account</Text>
          </CardHeader>
          <CardContent>
            <Box gap="m">
              {/* First Name */}
              <Box gap="s">
                <Label>First Name *</Label>
                <Input
                  value={firstName}
                  onChangeText={setFirstName}
                  placeholder="Enter your first name"
                  editable={!updateProfileMutation.isPending}
                />
              </Box>

              {/* Last Name */}
              <Box gap="s">
                <Label>Last Name *</Label>
                <Input
                  value={lastName}
                  onChangeText={setLastName}
                  placeholder="Enter your last name"
                  editable={!updateProfileMutation.isPending}
                />
              </Box>

              {/* Email (Read-only) */}
              <Box gap="s">
                <Label>Email Address</Label>
                <Box flexDirection="row" alignItems="center" gap="s">
                  <Mail size={16} color={theme.colors.textSecondary} />
                  <Input
                    value={user.email || ''}
                    editable={false}
                    style={styles.disabledInput}
                  />
                </Box>
                <Text variant="caption">
                  Email cannot be changed as it is managed by your authentication provider.
                </Text>
              </Box>

              {/* Default League */}
              <Box gap="s">
                <Label>Default League</Label>
                {user.leagues?.length ? (
                  <>
                    <AppSelect
                      value={defaultLeagueId ?? NO_DEFAULT_LEAGUE_VALUE}
                      onChange={(value) => {
                        if (value === NO_DEFAULT_LEAGUE_VALUE) {
                          setDefaultLeagueId(null);
                          return;
                        }
                        setDefaultLeagueId(value);
                      }}
                      options={[
                        { value: NO_DEFAULT_LEAGUE_VALUE, label: 'Use first available league' },
                        ...user.leagues.map((league) => ({
                          value: String(league.id),
                          label: league.name,
                        })),
                      ]}
                      placeholder="Choose a league"
                    />
                    <Text variant="caption">
                      We&apos;ll open this league whenever you sign in.
                    </Text>
                  </>
                ) : (
                  <Text variant="bodySmall" color="pressMuted">
                    Join a league to set a default.
                  </Text>
                )}
              </Box>

              {/* Save Button */}
              <Button
                onPress={handleSaveProfile}
                disabled={!hasChanges || updateProfileMutation.isPending}
                marginTop="m"
              >
                <Box flexDirection="row" alignItems="center" justifyContent="center" gap="s">
                  {updateProfileMutation.isPending ? (
                    <ActivityIndicator size="small" color={theme.colors.white} />
                  ) : (
                    <Save size={16} color={theme.colors.white} />
                  )}
                  <Text variant="button" color="white">
                    {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </Text>
                </Box>
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Notification Preferences */}
        <Card marginHorizontal="m" marginBottom="m">
          <CardHeader>
            <Box flexDirection="row" alignItems="center" gap="s">
              <Bell size={16} color={theme.colors.textSecondary} />
              <Text variant="caption">Notifications</Text>
            </Box>
          </CardHeader>
          <CardContent>
            <Box gap="m">
              <Box flexDirection="row" alignItems="center" justifyContent="space-between" gap="m">
                <Box flex={1}>
                  <Text variant="body" fontWeight="medium">Pick reminders</Text>
                  <Text variant="caption" color="pressMuted">
                    Remind me when picks are due.
                  </Text>
                </Box>
                <Switch
                  checked={pushPicksRemindersEnabled}
                  onCheckedChange={setPushPicksRemindersEnabled}
                  disabled={updateProfileMutation.isPending}
                />
              </Box>
              <Box flexDirection="row" alignItems="center" justifyContent="space-between" gap="m">
                <Box flex={1}>
                  <Text variant="body" fontWeight="medium">Live game alerts</Text>
                  <Text variant="caption" color="pressMuted">Close games and upset watches.</Text>
                </Box>
                <Switch checked={pushLiveGameAlertsEnabled} onCheckedChange={setPushLiveGameAlertsEnabled} disabled={updateProfileMutation.isPending} />
              </Box>
              <Box flexDirection="row" alignItems="center" justifyContent="space-between" gap="m">
                <Box flex={1}>
                  <Text variant="body" fontWeight="medium">My pick updates</Text>
                  <Text variant="caption" color="pressMuted">Prop leaders and game bet swings.</Text>
                </Box>
                <Switch checked={pushMyPickUpdatesEnabled} onCheckedChange={setPushMyPickUpdatesEnabled} disabled={updateProfileMutation.isPending} />
              </Box>
              <Box flexDirection="row" alignItems="center" justifyContent="space-between" gap="m">
                <Box flex={1}>
                  <Text variant="body" fontWeight="medium">Final scores</Text>
                  <Text variant="caption" color="pressMuted">Completed-game score updates.</Text>
                </Box>
                <Switch checked={pushFinalScoresEnabled} onCheckedChange={setPushFinalScoresEnabled} disabled={updateProfileMutation.isPending} />
              </Box>
              <Box gap="s">
                <Label>Live notification delay</Label>
                <AppSelect
                  value={String(liveNotificationDelayMinutes)}
                  onChange={(value) => setLiveNotificationDelayMinutes(Number(value))}
                  options={[0, 1, 3, 5, 10, 15].map((minutes) => ({ value: String(minutes), label: minutes === 0 ? 'Live' : `${minutes} minutes` }))}
                  placeholder="Live"
                />
                <Text variant="caption" color="pressMuted">Delay score-related NFL alerts to avoid game spoilers.</Text>
              </Box>
              <Box flexDirection="row" alignItems="center" justifyContent="space-between" gap="m">
                <Box flex={1}>
                  <Text variant="body" fontWeight="medium">League chat messages</Text>
                  <Text variant="caption" color="pressMuted">
                    Notify me when someone sends a new message in league chat.
                  </Text>
                </Box>
                <Switch
                  checked={pushChatMessagesEnabled}
                  onCheckedChange={setPushChatMessagesEnabled}
                  disabled={updateProfileMutation.isPending}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Admin Tools */}
        {(isAdmin || isBetsAdmin) && (
          <Card marginHorizontal="m" marginBottom="m">
            <CardHeader>
              <Box flexDirection="row" alignItems="center" gap="s">
                <Shield size={16} color={theme.colors.textSecondary} />
                <Text variant="caption">Admin tools</Text>
              </Box>
            </CardHeader>
            <CardContent>
              <Box gap="s">
                {isAdmin && (
                  <PressableBox
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                    paddingVertical="m"
                    borderBottomWidth={1}
                    borderColor="border"
                    onPress={() => router.push(getWeekManagementHref())}
                  >
                    <Box flexDirection="row" alignItems="center" gap="m">
                      <Shield size={20} color={theme.colors.press} />
                      <Box>
                        <Text variant="body" fontWeight="medium">Week Management</Text>
                        <Text variant="caption" color="pressMuted">Admin settings for NFL weeks</Text>
                      </Box>
                    </Box>
                    <ChevronRight size={20} color={theme.colors.press} />
                  </PressableBox>
                )}
                {isAdmin && (
                  <PressableBox
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                    paddingVertical="m"
                    borderBottomWidth={isBetsAdmin ? 1 : 0}
                    borderColor="border"
                    onPress={() => router.push(getFeedbackHref())}
                  >
                    <Box flexDirection="row" alignItems="center" gap="m">
                      <MessageSquareMore size={20} color={theme.colors.press} />
                      <Box>
                        <Text variant="body" fontWeight="medium">Feedback</Text>
                        <Text variant="caption" color="pressMuted">Review submitted web feedback</Text>
                      </Box>
                    </Box>
                    <ChevronRight size={20} color={theme.colors.press} />
                  </PressableBox>
                )}
                {isBetsAdmin && (
                  <PressableBox
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                    paddingVertical="m"
                    onPress={() => router.push(getBetsCreatorHref())}
                  >
                    <Box flexDirection="row" alignItems="center" gap="m">
                      <ClipboardList size={20} color={theme.colors.press} />
                      <Box>
                        <Text variant="body" fontWeight="medium">Bets Creator</Text>
                        <Text variant="caption" color="pressMuted">Manage custom prop bets</Text>
                      </Box>
                    </Box>
                    <ChevronRight size={20} color={theme.colors.press} />
                  </PressableBox>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        <Box marginHorizontal="m" marginBottom="l">
          <Button
            onPress={handleLogout}
            variant="outline"
            borderColor="destructive"
            style={styles.logoutButton}
            accessibilityRole="button"
            accessibilityLabel="Logout"
          >
            <Box flexDirection="row" alignItems="center" justifyContent="center" gap="s">
              <LogOut size={18} color={theme.colors.destructive} />
              <Text variant="button" color="destructive">Logout</Text>
            </Box>
          </Button>
        </Box>
      </ScrollView>
    </AppSafeArea>
  );
}

const styles = StyleSheet.create(theme => ({
  disabledInput: {
    backgroundColor: theme.colors.paperDark,
  },
  logoutButton: {
    backgroundColor: theme.colors.errorBackground,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: theme.spacing.xl,
  },
}));
